import store from '@/state/store'

export default [
    {
        path: '/login',
        name: 'login',
        component: () => import('../views/pages/account/login'),
        meta: {
            beforeResolve(routeTo, routeFrom, next) {
                // If the user is already logged in
                if (store.getters['auth/loggedIn']) {
                    // Redirect to the home page instead
                    next({ name: 'home' })
                } else {
                    // Continue to the login page
                    next()
                }
            },
        },
    },
    {
        path: '/unauthorized',
        name: 'unauthorized',
        component: () => import('../views/pages/utility/unauthorized'),
        meta: {
            authRequired: true,
        }
    },
    {
        path: '/',
        name: 'home',
        meta: {
            authRequired: true,
            beforeResolve(routeTo, routeFrom, next) {
                // Get user role from localStorage
                let userRole = 'patient'; // Default role
                
                // Try to get role from userInfo first, then from user
                const userInfo = localStorage.getItem('userInfo');
                const loggeduser = localStorage.getItem('user');
                
                if (userInfo) {
                    try {
                        const userInfoData = JSON.parse(userInfo);
                        userRole = userInfoData.role ? userInfoData.role.toLowerCase() : 'patient';
                    } catch (e) {
                        console.error('Error parsing userInfo:', e);
                    }
                } else if (loggeduser) {
                    try {
                        const userData = JSON.parse(loggeduser);
                        userRole = userData.role ? userData.role.toLowerCase() : 'patient';
                    } catch (e) {
                        console.error('Error parsing user:', e);
                    }
                }

                // Redirect to role-specific dashboard
                if (userRole === 'admin') {
                    next({ name: 'admin-dashboard-advanced' });
                } else if (userRole === 'doctor') {
                    next({ name: 'doctor-dashboard' });
                } else if (userRole === 'technician') {
                    next({ name: 'technician-dashboard' });
                } else {
                    next({ name: 'patient-dashboard' });
                }
            },
        },
        component: () => import('../views/pages/dashboard/index')
    },
    {
        path: '/chat',
        name: 'Chat',
        meta: {
            authRequired: true,
        },
        component: () => import('../views/pages/chat/index')
    },

    // Profile Route (available for all authenticated users)
    {
        path: '/profile',
        name: 'user-profile',
        meta: {
            authRequired: true,
        },
        component: () => import('../views/pages/profile/index')
    },

    // Admin Routes
    {
        path: '/admin/dashboard',
        name: 'admin-dashboard',
        meta: {
            authRequired: true,
            requiredRole: 'admin'
        },
        component: () => import('../views/pages/admin/dashboard')
    },
    {
        path: '/admin/dashboard-advanced',
        name: 'admin-dashboard-advanced',
        meta: {
            authRequired: true,
            requiredRole: 'admin'
        },
        component: () => import('../views/pages/admin/dashboard-advanced')
    },
    {
        path: '/admin/debug-auth',
        name: 'admin-debug-auth',
        meta: {
            authRequired: true,
            requiredRole: 'admin'
        },
        component: () => import('../views/pages/admin/debug-auth')
    },
    {
        path: '/admin/users/create',
        name: 'admin-create-user',
        meta: {
            authRequired: true,
            requiredRole: 'admin'
        },
        component: () => import('../views/pages/admin/create-user')
    },
    {
        path: '/admin/users/:id/edit',
        name: 'admin-edit-user',
        meta: {
            authRequired: true,
            requiredRole: 'admin'
        },
        component: () => import('../views/pages/admin/edit-user')
    },
    {
        path: '/admin/users',
        name: 'admin-users',
        meta: {
            authRequired: true,
            requiredRole: 'admin'
        },
        component: () => import('../views/pages/admin/users')
    },
    {
        path: '/admin/status',
        name: 'admin-status',
        meta: {
            authRequired: true,
            requiredRole: 'admin'
        },
        component: () => import('../views/pages/admin/status')
    },

    // Configuration Routes (Admin Only)
    {
        path: '/configuration/exam-types',
        name: 'configuration-exam-types',
        meta: {
            authRequired: true,
            requiredRole: 'admin'
        },
        component: () => import('../views/pages/configuration/exam-types/ExamTypesList')
    },
    {
        path: '/configuration/exam-types/new',
        name: 'configuration-exam-types-new',
        meta: {
            authRequired: true,
            requiredRole: 'admin'
        },
        component: () => import('../views/pages/configuration/exam-types/ExamTypeForm')
    },
    {
        path: '/configuration/exam-types/edit/:id',
        name: 'configuration-exam-types-edit',
        meta: {
            authRequired: true,
            requiredRole: 'admin'
        },
        component: () => import('../views/pages/configuration/exam-types/ExamTypeForm')
    },
    {
        path: '/configuration/exam-types/view/:id',
        name: 'configuration-exam-types-view',
        meta: {
            authRequired: true,
            requiredRole: 'admin'
        },
        component: () => import('../views/pages/configuration/exam-types/ExamTypeView')
    },

    // Doctor Routes
    {
        path: '/doctor/dashboard',
        name: 'doctor-dashboard',
        meta: {
            authRequired: true,
            requiredRole: 'doctor'
        },
        component: () => import('../views/pages/doctor/dashboard')
    },
    {
        path: '/doctor/exams',
        name: 'doctor-exams',
        meta: {
            authRequired: true,
            requiredRole: 'doctor'
        },
        component: () => import('../views/pages/doctor/exams')
    },
    {
        path: '/doctor/reports/create',
        name: 'doctor-create-report',
        meta: {
            authRequired: true,
            requiredRole: 'doctor'
        },
        component: () => import('../views/pages/doctor/create-report')
    },
    {
        path: '/doctor/reports/sign',
        name: 'doctor-sign-reports',
        meta: {
            authRequired: true,
            requiredRole: 'doctor'
        },
        component: () => import('../views/pages/doctor/sign-reports')
    },

    // Patient Routes
    {
        path: '/patients/create',
        name: 'patients-create',
        meta: {
            authRequired: true,
            requiredRole: ['admin', 'technician']
        },
        component: () => import('../views/pages/patients/create')
    },
    {
        path: '/patients',
        name: 'patients-list',
        meta: {
            authRequired: true,
            requiredRole: ['admin', 'doctor', 'technician']
        },
        component: () => import('../views/pages/patients/list')
    },
    {
        path: '/patients/search',
        name: 'patients-search',
        meta: {
            authRequired: true,
            requiredRole: ['admin', 'doctor', 'technician']
        },
        component: () => import('../views/pages/patients/search')
    },
    {
        path: '/patients/update-cpf',
        name: 'patients-update-cpf',
        meta: {
            authRequired: true,
            requiredRole: ['admin', 'technician']
        },
        component: () => import('../views/pages/patients/update-cpf')
    },
    {
        path: '/patient/dashboard',
        name: 'patient-dashboard',
        meta: {
            authRequired: true,
            requiredRole: 'patient'
        },
        component: () => import('../views/pages/patient/dashboard')
    },
    {
        path: '/patient/my-exams',
        name: 'patient-my-exams',
        meta: {
            authRequired: true,
            requiredRole: 'patient'
        },
        component: () => import('../views/pages/patients/my-exams')
    },
    {
        path: '/patient/my-reports',
        name: 'patient-my-reports',
        meta: {
            authRequired: true,
            requiredRole: 'patient'
        },
        component: () => import('../views/pages/patients/my-reports')
    },

    // Technician Routes
    {
        path: '/technician/dashboard',
        name: 'technician-dashboard',
        meta: {
            authRequired: true,
            requiredRole: 'technician'
        },
        component: () => import('../views/pages/technician/dashboard')
    },
    {
        path: '/technician/upload-dicom',
        name: 'technician-upload-dicom',
        meta: {
            authRequired: true,
            requiredRole: 'technician'
        },
        component: () => import('../views/pages/technician/upload-dicom')
    },
    {
        path: '/technician/patients/create',
        name: 'technician-create-patient',
        meta: {
            authRequired: true,
            requiredRole: 'technician'
        },
        component: () => import('../views/pages/technician/create-patient')
    },
    {
        path: '/technician/exams/create',
        name: 'technician-create-exam',
        meta: {
            authRequired: true,
            requiredRole: 'technician'
        },
        component: () => import('../views/pages/technician/create-exam')
    },
    {
        path: '/technician/exams',
        name: 'technician-exams',
        meta: {
            authRequired: true,
            requiredRole: 'technician'
        },
        component: () => import('../views/pages/technician/exams')
    },

    // DICOM Routes
    {
        path: '/dicom/upload',
        name: 'dicom-upload',
        meta: {
            authRequired: true,
            requiredRole: ['admin', 'technician']
        },
        component: () => import('../views/pages/dicom/upload')
    },
    {
        path: '/dicom/viewer',
        name: 'dicom-viewer',
        meta: {
            authRequired: true,
            requiredRole: ['admin', 'doctor', 'technician']
        },
        component: () => import('../views/pages/dicom/viewer')
    },
    {
        path: '/viewer/:study_uid',
        name: 'ohif-viewer',
        meta: {
            authRequired: true,
            requiredRole: ['admin', 'doctor', 'technician'],
            requiresStudyAccess: true
        },
        component: () => import('../views/pages/dicom/ohif-viewer'),
        props: true
    },
    {
        path: '/viewer/:study_uid/series/:series_uid',
        name: 'ohif-viewer-series',
        meta: {
            authRequired: true,
            requiredRole: ['admin', 'doctor', 'technician'],
            requiresStudyAccess: true
        },
        component: () => import('../views/pages/dicom/ohif-viewer'),
        props: true
    },
    {
        path: '/dicom/studies',
        name: 'dicom-studies',
        meta: {
            authRequired: true,
            requiredRole: ['admin', 'doctor', 'technician']
        },
        component: () => import('../views/pages/dicom/studies')
    },

    // Exam Routes
    {
        path: '/exams/create',
        name: 'exams-create',
        meta: {
            authRequired: true,
            requiredRole: ['admin', 'technician']
        },
        component: () => import('../views/pages/exams/create')
    },
    {
        path: '/exams',
        name: 'exams-list',
        meta: {
            authRequired: true,
            requiredRole: ['admin', 'doctor', 'technician']
        },
        component: () => import('../views/pages/exams/list')
    },
    {
        path: '/exams/search',
        name: 'exams-search',
        meta: {
            authRequired: true,
            requiredRole: ['admin', 'doctor', 'technician']
        },
        component: () => import('../views/pages/exams/search')
    },
    {
        path: '/exams/by-patient/:patientId?',
        name: 'exams-by-patient',
        meta: {
            authRequired: true,
            requiredRole: ['admin', 'doctor', 'technician']
        },
        component: () => import('../views/pages/exams/by-patient')
    },
    {
        path: '/exams/laboratory-report',
        name: 'exams-laboratory-report',
        meta: {
            authRequired: true,
            requiredRole: ['admin', 'doctor', 'technician']
        },
        component: () => import('../views/pages/exams/laboratory-report')
    },
    {
        path: '/exams/reports',
        name: 'exams-reports',
        meta: {
            authRequired: true,
            requiredRole: ['admin', 'doctor']
        },
        component: () => import('../views/pages/exams/reports')
    },

    // Integration Routes
    {
        path: '/hl7/messages',
        name: 'hl7-messages',
        meta: {
            authRequired: true,
            requiredRole: 'admin'
        },
        component: () => import('../views/pages/integration/hl7-messages')
    },
    {
        path: '/hl7/settings',
        name: 'hl7-settings',
        meta: {
            authRequired: true,
            requiredRole: 'admin'
        },
        component: () => import('../views/pages/integration/hl7-settings')
    },
    {
        path: '/system/logs',
        name: 'system-logs',
        meta: {
            authRequired: true,
            requiredRole: 'admin'
        },
        component: () => import('../views/pages/integration/system-logs')
    },
    {
        path: '/fhir/config',
        name: 'fhir-config',
        meta: {
            authRequired: true,
            requiredRole: 'admin'
        },
        component: () => import('../views/pages/integration/fhir-config')
    },
    {
        path: '/fhir/logs',
        name: 'fhir-logs',
        meta: {
            authRequired: true,
            requiredRole: 'admin'
        },
        component: () => import('../views/pages/integration/fhir-logs')
    },
    {
        path: '/fhir/export-esus',
        name: 'fhir-export-esus',
        meta: {
            authRequired: true,
            requiredRole: ['admin', 'doctor']
        },
        component: () => import('../views/pages/integration/export-esus')
    },
    
]
